import './styles.scss';

import { faCheck, faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Grid, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import React, { useMemo, useState } from 'react';

import BitcoinIcon from '../../../../assets/icons/currency/BTC.svg';
import EthereumIcon from '../../../../assets/icons/currency/ETH.svg';
import SolanaIcon from '../../../../assets/icons/currency/Solana.svg';
import RippleIcon from '../../../../assets/icons/currency/XRP.svg';
import AvaxIcon from '../../../../assets/icons/currency/AVAX.svg';
import TrxIcon from '../../../../assets/icons/currency/TRX.svg';
import HypeIcon from '../../../../assets/icons/currency/HYPE.png';
import { CurrencyTypeEnum, getCurrencyName } from '../../../../model/currency_type.enum';
import { DashboardTabsKeysEnum } from '../../../../model/dashboard-tabs';
import { DataLabelEnum } from '../../../../model/data_label.enum';
import { DashboardContext } from '../../../../providers/dashboard-provider';
import { OverviewContext } from '../../../../providers/overview-provider';
import { gray } from '../../../../theme/colors';

type TokenSelectorProps = {
  currency: CurrencyTypeEnum;
  setCurrency?: React.Dispatch<React.SetStateAction<CurrencyTypeEnum>>;
};

type MenuItem = {
  currency: CurrencyTypeEnum;
  label: string;
  icon: string;
};

const menuItems: MenuItem[] = [
  {
    currency: CurrencyTypeEnum.BITCOIN,
    label: `${CurrencyTypeEnum.BITCOIN} Deribit`,
    icon: BitcoinIcon,
  },
  {
    currency: CurrencyTypeEnum.ETHEREUM,
    label: `${CurrencyTypeEnum.ETHEREUM} Deribit`,
    icon: EthereumIcon,
  },
  {
    currency: CurrencyTypeEnum.SOL,
    label: `${CurrencyTypeEnum.SOL} Deribit`,
    icon: SolanaIcon,
  },
  {
    currency: CurrencyTypeEnum.XRP,
    label: `${CurrencyTypeEnum.XRP} Deribit`,
    icon: RippleIcon,
  },
  {
    currency: CurrencyTypeEnum.AVAX_USDC,
    label: `${CurrencyTypeEnum.AVAX_USDC} Deribit`,
    icon: AvaxIcon,
  },
  {
    currency: CurrencyTypeEnum.TRX_USDC,
    label: `${CurrencyTypeEnum.TRX_USDC} Deribit`,
    icon: TrxIcon,
  },
  {
    currency: CurrencyTypeEnum.HYPE,
    label: `${CurrencyTypeEnum.HYPE} Deribit`,
    icon: HypeIcon,
  },
];

const TokenSelector = ({ currency, setCurrency }: TokenSelectorProps) => {
  const { setCurrency: overviewSetCurrency, label, setLabel } = React.useContext(OverviewContext);
  const { source, addTab, selectTab } = React.useContext(DashboardContext);

  const getImage = () => {
    switch (currency) {
      case CurrencyTypeEnum.BITCOIN:
        return <img alt="Bitcoin" src={BitcoinIcon} />;
      case CurrencyTypeEnum.ETHEREUM:
        return <img alt="Ethereum" src={EthereumIcon} />;
      case CurrencyTypeEnum.SOL:
        return <img alt="Solana" src={SolanaIcon} />;
      case CurrencyTypeEnum.XRP:
        return <img alt="Ripple" src={RippleIcon} />;
      case CurrencyTypeEnum.AVAX_USDC:
        return <img alt="Avalanche" src={AvaxIcon} />;
      case CurrencyTypeEnum.TRX_USDC:
        return <img alt="Tron" src={TrxIcon} />;
      case CurrencyTypeEnum.HYPE:
        return <img alt="Hyperliquid" src={HypeIcon} />;
    }
  };

  const formatCurrency = (token: CurrencyTypeEnum) => {
    if (token?.endsWith('_USDC')) {
      return token.slice(0, -5);
    }
    return token;
  };

  const styleObj = {
    borderRadius: 0,
    '&:hover': {
      backgroundColor: gray[9],
      borderRadius: 0,
    },
    '&:active': {
      backgroundColor: gray[9],
      borderRadius: 0,
    },
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    c: CurrencyTypeEnum,
    l: DataLabelEnum,
  ) => {
    if (setCurrency) {
      setCurrency(c);
      handleClose();
      return;
    }

    overviewSetCurrency(c);

    setLabel(l);

    if (c !== CurrencyTypeEnum.BITCOIN) {
      const newTab = c;

      const newTabKey = `overview_${newTab.toLowerCase()}`;
      const newTabLabel = `${formatCurrency(newTab)} Overview`;

      addTab(newTabKey, newTabLabel);
    } else {
      selectTab(DashboardTabsKeysEnum.overviewBTC);
    }

    handleClose();
  };

  const renderTokenSelector = useMemo(() => {
    const renderMenuItem = (item: MenuItem) => (
      <MenuItem
        selected={currency === item.currency && label === DataLabelEnum.PERPETUAL}
        onClick={(event) => handleChange(event, item.currency, DataLabelEnum.PERPETUAL)}
        key={item.currency}
      >
        <ListItemIcon>
          <img alt={item.label} src={item.icon} />
        </ListItemIcon>
        <ListItemText disableTypography className="token-menu-item">
          {item.label}
        </ListItemText>
        <ListItemIcon className="selected-check-icon">
          {currency === item.currency && label === DataLabelEnum.PERPETUAL && (
            <FontAwesomeIcon icon={faCheck} />
          )}
        </ListItemIcon>
      </MenuItem>
    );

    return (
      <>
        <Button
          onClick={handleClick}
          aria-controls={open ? 'token-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          variant="text"
          disableRipple
          sx={styleObj}
          className="token-selector-button"
          startIcon={getImage()}
          endIcon={<FontAwesomeIcon icon={faChevronDown} />}
        >
          <Grid container direction="column" spacing={0}>
            <Grid item className="selected-token-name">
              {getCurrencyName(currency)}
            </Grid>
            <Grid item className="selected-token-data">
              {`${currency}`} {source.charAt(0).toUpperCase() + source.toLowerCase().slice(1)}
            </Grid>
          </Grid>
        </Button>

        <Menu
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          sx={{ marginTop: '2rem', marginLeft: '1.5rem' }}
          className="token-selector-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          {menuItems.map((item) => renderMenuItem(item))}
        </Menu>
      </>
    );
  }, [anchorEl]);

  return <>{renderTokenSelector}</>;
};

export default TokenSelector;
